import { Component, computed, DestroyRef, effect, Signal, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ExportChannel } from 'exports/interfaces/export-channels.interface';
import { camelCase } from 'lodash-es';
import { startWith } from 'rxjs';
import { BrandRestrictiveUiType, UserPreferenceRestrictiveUiType } from 'settings/enums/brand-restrictive-ui.enum';
import { ChannelRestrictions } from 'settings/interfaces/brand.interface';
import { ChannelsService } from 'settings/services/channels.service';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { RESTRICTIVE_UI_LISTINGS } from 'shared/constants/storage-keys';
import { FormDifferService, StorageService } from 'shared/services';

interface RestrictiveUiTypeOption {
    label: string;
    value: UserPreferenceRestrictiveUiType;
}

@Component({
    selector: 'app-listing-user-preferences-restrictive-ui',
    templateUrl: './listing-user-preferences-restrictive-ui.component.html',
    styleUrls: ['./listing-user-preferences-restrictive-ui.component.scss'],
    imports: [TranslateModule, ReactiveFormsModule, FormErrorComponent],
})
export class ListingUserPreferencesRestrictiveUiComponent implements OnInit {
    public data!: {
        listingId: string;
        listingRestrictiveUiType: BrandRestrictiveUiType;
        brandRestrictiveUiChannels: ChannelRestrictions[];
        closeModal?: (data?: any) => void;
    };

    public restrictiveUiForm = new UntypedFormGroup({
        listingRestrictiveUIType: new UntypedFormControl(null, Validators.required),
        listingRestrictiveUISelectedChannel: new UntypedFormControl(null),
    });

    public userPreferenceRestrictiveUiType = UserPreferenceRestrictiveUiType;
    public brandRestrictiveUiType = BrandRestrictiveUiType;

    private langChangeSignal = toSignal(this.translate.onLangChange, {
        initialValue: {
            lang: this.translate.getCurrentLang() || this.translate.defaultLang,
            translations: {},
        } as LangChangeEvent,
        requireSync: false,
    });

    public listingRestrictiveUITypeOptions = computed<RestrictiveUiTypeOption[]>(() => {
        this.langChangeSignal();
        return Object.keys(UserPreferenceRestrictiveUiType).map((key) => ({
            label: this.translate.instant(`listings.userPreferences.brands.brandViewSettings.type.${camelCase(key)}`),
            value: UserPreferenceRestrictiveUiType[key as keyof typeof UserPreferenceRestrictiveUiType],
        }));
    });

    public allChannelsForExport: Signal<ExportChannel[]> = toSignal(
        this.channelsService.getChannelsForFullExport().pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: [] },
    );

    public restrictiveUiChannelsFromBrand = signal<ExportChannel[]>([]);

    private selectedUserRestrictiveTypeSignal = toSignal(
        this.restrictiveUiForm.controls.listingRestrictiveUIType.valueChanges.pipe(
            startWith(this.restrictiveUiForm.controls.listingRestrictiveUIType.value),
            takeUntilDestroyed(this.destroyRef),
        ),
    );

    public restrictiveUiChannels = computed<ExportChannel[]>(() => {
        const selectedUserTypeOption = this.selectedUserRestrictiveTypeSignal();
        const selectedUserTypeValue = selectedUserTypeOption?.value;

        const brandType = this.data?.listingRestrictiveUiType;

        return this.setRestrictiveUiChannelsBasedOnSelectedUserType(brandType, selectedUserTypeValue);
    });

    constructor(
        private storageService: StorageService,
        private formService: FormDifferService,
        private destroyRef: DestroyRef,
        private translate: TranslateService,
        private channelsService: ChannelsService,
    ) {
        effect(() => {
            if (this.allChannelsForExport()) {
                this.setListingRestrictiveUIPreferences();
            }
        });

        effect(() => {
            const allChannels = this.allChannelsForExport();
            const brandRestrictiveUiChannels = this.data?.brandRestrictiveUiChannels;

            if (allChannels.length && brandRestrictiveUiChannels?.length) {
                const matchedChannels = allChannels.filter((channel) =>
                    brandRestrictiveUiChannels.some((restrictiveChannel) => restrictiveChannel.channelId === channel.value),
                );

                this.restrictiveUiChannelsFromBrand.set(matchedChannels);
            }
        });
    }

    ngOnInit(): void {
        this.formService.registerForm(this.restrictiveUiForm);
        this.formService.createDataSnapshot();
        this.setListingRestrictiveUIPreferences();

        computed(() => {
            const selectedUserType = this.selectedUserRestrictiveTypeSignal();
            const channelFormControl = this.restrictiveUiForm.controls.listingRestrictiveUISelectedChannel;

            if (selectedUserType?.value === UserPreferenceRestrictiveUiType.CUSTOM) {
                if (channelFormControl.disabled) {
                    channelFormControl.enable({ emitEvent: false });
                }
            } else {
                if (channelFormControl.enabled) {
                    channelFormControl.disable({ emitEvent: false });
                }
                channelFormControl.setValue(null, { emitEvent: false });
            }
        });
    }

    setRestrictiveUiChannelsBasedOnSelectedUserType(brandType: BrandRestrictiveUiType, selectedUserTypeValue: any) {
        switch (selectedUserTypeValue) {
            case UserPreferenceRestrictiveUiType.SAME_AS_BRAND:
                return [];
            case UserPreferenceRestrictiveUiType.CUSTOM:
                return this.setRestrictiveUiChannelsForCustomTypeAndBrandTypes(brandType);
            default:
                return [];
        }
    }

    setRestrictiveUiChannelsForCustomTypeAndBrandTypes(brandType: BrandRestrictiveUiType) {
        switch (brandType) {
            case BrandRestrictiveUiType.DEFAULT:
                return this.allChannelsForExport();
            case BrandRestrictiveUiType.RESTRICTIVE:
                return this.restrictiveUiChannelsFromBrand();
            default:
                return [];
        }
    }

    setListingRestrictiveUIPreferences() {
        const currentRestrictiveUIPreferencesFromStorage = this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS);
        const listingRestrictiveUITypeOptions = this.listingRestrictiveUITypeOptions();

        if (currentRestrictiveUIPreferencesFromStorage) {
            const parsedRestrictiveUIPreferences = JSON.parse(currentRestrictiveUIPreferencesFromStorage as string);
            const storedChannelId = parsedRestrictiveUIPreferences[this.data.listingId]?.toString();

            if (storedChannelId) {
                const customTypeOption = listingRestrictiveUITypeOptions.find(
                    (option) => option.value === UserPreferenceRestrictiveUiType.CUSTOM,
                );

                if (customTypeOption) {
                    this.restrictiveUiForm.get('listingRestrictiveUIType')?.setValue(customTypeOption);

                    const channelListToSearch: ExportChannel[] = this.setRestrictiveUiChannelsForCustomTypeAndBrandTypes(
                        this.data.listingRestrictiveUiType,
                    );
                    const channelWithRestrictiveUI = channelListToSearch.find((channel) => {
                        if ('channelId' in channel) {
                            return (channel as ChannelRestrictions).channelId?.toString() === storedChannelId;
                        }
                        if ('value' in channel) {
                            return (channel as ExportChannel).value?.toString() === storedChannelId;
                        }
                        return false;
                    });

                    this.restrictiveUiForm.get('listingRestrictiveUISelectedChannel')?.setValue(channelWithRestrictiveUI ?? null);
                } else {
                    this.restrictiveUiForm.get('listingRestrictiveUIType')?.setValue(null);
                }
            } else {
                const sameAsBrandTypeOption = listingRestrictiveUITypeOptions.find(
                    (option) => option.value === UserPreferenceRestrictiveUiType.SAME_AS_BRAND,
                );
                this.restrictiveUiForm.get('listingRestrictiveUIType')?.setValue(sameAsBrandTypeOption ?? null);
            }
        } else {
            const sameAsBrandTypeOption = listingRestrictiveUITypeOptions.find(
                (option) => option.value === UserPreferenceRestrictiveUiType.SAME_AS_BRAND,
            );
            this.restrictiveUiForm.get('listingRestrictiveUIType')?.setValue(sameAsBrandTypeOption ?? null);
        }
    }

    saveListingRestrictiveUIPreferences() {
        if (this.restrictiveUiForm.invalid) {
            this.restrictiveUiForm.markAllAsTouched();
            return;
        }

        const currentRestrictiveUIPreferencesFromStorage = this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) || '{}';
        const currentRestrictiveUIPreferencesFromStorageParsed = JSON.parse(currentRestrictiveUIPreferencesFromStorage as string);

        const selectedType = this.restrictiveUiForm.get('listingRestrictiveUIType')?.value?.value;

        switch (selectedType) {
            case UserPreferenceRestrictiveUiType.SAME_AS_BRAND: {
                if (currentRestrictiveUIPreferencesFromStorageParsed[this.data.listingId]) {
                    delete currentRestrictiveUIPreferencesFromStorageParsed[this.data.listingId];
                }
                break;
            }
            case UserPreferenceRestrictiveUiType.CUSTOM: {
                const selectedChannel = this.restrictiveUiForm.get('listingRestrictiveUISelectedChannel')?.value as ExportChannel | null;
                if (selectedChannel?.value) {
                    currentRestrictiveUIPreferencesFromStorageParsed[this.data.listingId] = selectedChannel.value;
                } else {
                    if (currentRestrictiveUIPreferencesFromStorageParsed[this.data.listingId]) {
                        delete currentRestrictiveUIPreferencesFromStorageParsed[this.data.listingId];
                    }
                }
                break;
            }
            default:
                break;
        }

        this.storageService.setInLocal(RESTRICTIVE_UI_LISTINGS, JSON.stringify(currentRestrictiveUIPreferencesFromStorageParsed));
        this.data.closeModal?.(true);
    }
}
